import React from "react";
import {
  CATEGORIES_ORDER,
  CATEGORY_COLORS,
  CATEGORY_MINIMUMS,
  getCategoryIndex,
  getNextCategoryInfo,
} from "../../utils/categoryColors";
import EstrelaCategoria from "./EstrelaCategoria";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/navigation";
import { Navigation } from "swiper";
import "./index.css";

// MARFIM tem override de cor no termômetro para contraste (mesma regra do header).
function corCategoria(cat) {
  return cat === "MARFIM" ? "#C9A06B" : CATEGORY_COLORS[cat];
}

function formatarReais(valor) {
  return (valor || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

function formatarPeriodo(dataInicio, dataFim) {
  if (!dataInicio || !dataFim) return "";
  const ini = new Date(dataInicio);
  const fim = new Date(dataFim);
  if (isNaN(ini.getTime()) || isNaN(fim.getTime())) return "";
  const dia = new Intl.DateTimeFormat("pt-BR", { day: "2-digit" });
  const mes = new Intl.DateTimeFormat("pt-BR", { month: "short" });
  const limpaMes = (d) => mes.format(d).replace(".", "");
  return `${dia.format(ini)} de ${limpaMes(ini)} - ${dia.format(fim)} de ${limpaMes(fim)}`;
}

export default function TermometroCategoria({ categoriaAtual, progressao }) {
  // Categoria vem da resposta da progressão (mais confiável que o localStorage,
  // que pode estar defasado); fallback para a prop.
  const categoria = progressao?.categoria || categoriaAtual;
  const activeIndex = getCategoryIndex(categoria);
  const hasCategory = activeIndex >= 0;

  // Só há progressão quando temos categoria pontuável E dados do período vigente.
  // Sem progressão (Participante Inativo, Professional Plus, sem categoria),
  // a mesma grade é renderizada toda em cinza, sem indicador.
  const temProgressao = hasCategory && !!progressao && !!progressao.dataFim;

  const frase = hasCategory
    ? "Quanto mais você cresce no programa, mais benefícios você ganha."
    : "Inicie sua jornada com uma compra na Leo Madeiras, para evoluir de categoria e aproveitar vantagens exclusivas em cada etapa.";

  const valorComprado = progressao?.valorComprado || 0;
  const periodo = temProgressao
    ? formatarPeriodo(progressao.dataInicio, progressao.dataFim)
    : "";
  // Alvo, quanto falta e fração preenchida (0..1) do segmento da categoria atual.
  // Na última categoria o alvo é o mínimo dela mesma (manter), não o da próxima.
  const { faltamReais, fracaoPreenchida, manterCategoria } = getNextCategoryInfo(
    categoria,
    valorComprado
  );

  // ---- Helpers de célula (3 faixas de altura fixa, barra sempre no mesmo Y) ----

  // Faixa TOPO: rótulo cinza (anteriores/futuras/sem progressão) OU balão "Você comprou*" (atual).
  function renderCellTop(cat, i) {
    const isAtual = temProgressao && i === activeIndex;
    const cor = corCategoria(cat);

    if (isAtual) {
      return (
        <div
          className="tc-balloon-anchor"
          style={{
            // clamp impede o balão de vazar pelas bordas da célula/card
            // (ex.: R$ 0,00 comprado => 0% => metade do balão para fora).
            left: `clamp(56px, ${fracaoPreenchida * 100}%, calc(100% - 56px))`,
          }}
        >
          <div className="tc-balloon" style={{ backgroundColor: cor }}>
            <span className="tc-balloon-caption">Você comprou*</span>
            <span className="tc-balloon-value">{formatarReais(valorComprado)}</span>
          </div>
          <div className="tc-balloon-arrow" style={{ borderTopColor: cor }} />
        </div>
      );
    }
    return <span className="tc-top-label">{cat}</span>;
  }

  // Faixa BARRA: régua contínua reta; célula por estado.
  function renderCellBar(cat, i) {
    const isAnterior = i < activeIndex;
    const isFutura = i > activeIndex;
    const cor = corCategoria(cat);

    // Anteriores e futuras ficam esmaecidas; só a atual fica em cor cheia.
    // Sem progressão, todas esmaecidas (cores da categoria, sem indicador).
    if (!temProgressao || isAnterior || isFutura)
      return (
        <div className="tc-bar-solid" style={{ backgroundColor: cor, opacity: 0.45 }} />
      );

    // atual
    return (
      <>
        <div
          className="tc-bar-fill"
          style={{ width: `${fracaoPreenchida * 100}%`, backgroundColor: cor }}
        />
        {fracaoPreenchida < 1 && (
          <div
            className="tc-bar-hatch"
            style={{ width: `${(1 - fracaoPreenchida) * 100}%` }}
          />
        )}
      </>
    );
  }

  // Célula da categoria ativa é mais larga (flex 2) nas 3 faixas, para o
  // segmento dela se destacar na régua mantendo as colunas alinhadas.
  function cellClass(i, extra) {
    return (
      "tc-cell" +
      (extra ? " " + extra : "") +
      (temProgressao && i === activeIndex ? " tc-cell-ativa" : "")
    );
  }

  // Faixa BASE: nome+estrela e "Faltam" (atual) / "Mínimo de compras" (futuras
  // e todas no modo sem progressão).
  function renderCellBottom(cat, i) {
    const isAtual = temProgressao && i === activeIndex;
    const isFutura = temProgressao ? i > activeIndex : true;

    if (isAtual) {
      return (
        <>
          <div className="tc-bottom-atual">
            <span className="tc-bottom-atual-nome">{cat}</span>
            <EstrelaCategoria categoria={cat} size={32} />
          </div>
          {faltamReais > 0 && (
            <span className="tc-bottom-faltam">
              {/* Duas linhas explícitas (itens do flex-column): a quebra não
                  depende da largura da célula nem do tamanho do valor
                  formatado, que varia de R$ 4.000,00 a R$ 204.000,00. */}
              <span>
                Faltam <b>{formatarReais(faltamReais)}</b>
              </span>
              {manterCategoria && <span>para manter a categoria</span>}
            </span>
          )}
        </>
      );
    }
    if (isFutura) {
      return (
        <div className="tc-bottom-min">
          <span className="tc-min-caption">Mínimo de compras</span>
          <span className="tc-min-value">{formatarReais(CATEGORY_MINIMUMS[cat])}</span>
        </div>
      );
    }
    return null; // anteriores: base vazia (rótulo é no topo)
  }

  return (
    <div className="termometro-container">
      {/* ---------- Desktop ---------- */}
      <div className="termometro-desktop">
        <p className="termometro-frase">{frase}</p>
        <div className="tc-grid">
          {periodo && <span className="termometro-periodo">{periodo}</span>}
          <div className="tc-row tc-row-top">
            {CATEGORIES_ORDER.map((cat, i) => (
              <div className={cellClass(i)} key={cat}>
                {renderCellTop(cat, i)}
              </div>
            ))}
          </div>
          <div className="tc-row tc-row-bar">
            {CATEGORIES_ORDER.map((cat, i) => (
              <div className={cellClass(i, "tc-bar-cell")} key={cat}>
                {renderCellBar(cat, i)}
              </div>
            ))}
          </div>
          <div className="tc-row tc-row-bottom">
            {CATEGORIES_ORDER.map((cat, i) => (
              <div className={cellClass(i)} key={cat}>
                {renderCellBottom(cat, i)}
              </div>
            ))}
          </div>
        </div>
        {temProgressao && (
          <p className="termometro-rodape">
            *As <b>notas fiscais</b> de compras são processadas em{" "}
            <b>até 72 horas</b> após a emissão.
          </p>
        )}
      </div>

      {/* ---------- Mobile (carrossel) ---------- */}
      {/* Ordem do programa preservada (anteriores à esquerda, superiores à direita);
          o carrossel abre no card da categoria do participante. A key força a
          remontagem se a categoria mudar depois (initialSlide só é lido ao montar). */}
      <div className="termometro-mobile">
        <p className="termometro-frase">{frase}</p>
        <Swiper
          key={activeIndex}
          modules={[Navigation]}
          navigation
          initialSlide={activeIndex >= 0 ? activeIndex : 0}
          slidesPerView={1.1}
          centeredSlides
          spaceBetween={12}
          className="termometro-swiper"
        >
          {CATEGORIES_ORDER.map((cat, i) => (
            <SwiperSlide key={cat}>
              <div className="termometro-card">
                {periodo && (
                  <span className="termometro-periodo-mobile">{periodo}</span>
                )}
                <div className="tc-grid tc-grid-single">
                  <div className="tc-row tc-row-top">
                    <div className="tc-cell">{renderCellTop(cat, i)}</div>
                  </div>
                  <div className="tc-row tc-row-bar">
                    <div className="tc-cell tc-bar-cell">
                      {renderCellBar(cat, i)}
                    </div>
                  </div>
                  <div className="tc-row tc-row-bottom">
                    <div className="tc-cell">{renderCellBottom(cat, i)}</div>
                  </div>
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
        {temProgressao && (
          <p className="termometro-rodape">
            *As <b>notas fiscais</b> de compras são processadas em{" "}
            <b>até 72 horas</b> após a emissão.
          </p>
        )}
      </div>
    </div>
  );
}
